import React, {FC, useEffect, useRef, useState} from 'react'
import {IJobModel} from '../job/JobModel'
import {IHrProcessModel} from '../process/HrProcessModel'
import {ITagModel} from '../tag/TagModel'

/** Arama 3 karakterden itibaren otomatik çalışır; alan temizlenince tüm kayıtlar gelir */
export const MIN_SEARCH_LENGTH = 3
const SEARCH_DELAY_MS = 400

export interface IListFilters {
  q: string
  jobId: string
  processId: string
  status: string
  tagId: string
}

export const emptyFilters: IListFilters = {q: '', jobId: '', processId: '', status: '', tagId: ''}

type Props = {
  placeholder?: string
  jobs?: IJobModel[]
  processes?: IHrProcessModel[]
  statusOptions?: {value: string; label: string}[]
  tags?: ITagModel[]
  /** Arama kutusu: yazdıkça (3+ karakter) çağrılır */
  onSearch: (q: string) => void
  /** Filtrele butonu: açılır liste seçimleriyle çağrılır */
  onFilter: (filters: Omit<IListFilters, 'q'>) => void
}

/**
 * Liste ekranlarının üst çubuğu: ad, soyad, e-posta, telefonda otomatik arama ve
 * "Filtrele" ile uygulanan ilan / süreç (/ durum) seçimleri.
 */
export const SearchFilterBar: FC<Props> = ({
  placeholder = 'Ad, soyad, e-posta veya telefon ile ara',
  jobs,
  processes,
  statusOptions,
  tags,
  onSearch,
  onFilter,
}) => {
  const [query, setQuery] = useState('')
  const [jobId, setJobId] = useState('')
  const [processId, setProcessId] = useState('')
  const [status, setStatus] = useState('')
  const [tagId, setTagId] = useState('')
  const lastSearched = useRef('')

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH) return
    if (trimmed === lastSearched.current) return
    const timer = setTimeout(() => {
      lastSearched.current = trimmed
      onSearch(trimmed)
    }, SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [query, onSearch])

  const handleClear = () => {
    setJobId('')
    setProcessId('')
    setStatus('')
    setTagId('')
    onFilter({jobId: '', processId: '', status: '', tagId: ''})
  }

  const hasDropdowns = !!jobs || !!processes || !!statusOptions || !!tags
  const hasSelection = !!jobId || !!processId || !!status || !!tagId

  return (
    <div className='d-flex flex-wrap align-items-center gap-3 mb-6'>
      <div className='position-relative flex-grow-1' style={{minWidth: 260, maxWidth: 420}}>
        <i className='bi bi-search position-absolute top-50 translate-middle-y ms-4 text-gray-500'></i>
        <input
          type='search'
          className='form-control form-control-solid ps-12'
          placeholder={placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {query.trim().length > 0 && query.trim().length < MIN_SEARCH_LENGTH && (
          <div className='form-text position-absolute'>En az {MIN_SEARCH_LENGTH} karakter giriniz</div>
        )}
      </div>

      {jobs && (
        <select
          className='form-select form-select-solid w-auto'
          style={{minWidth: 200}}
          value={jobId}
          onChange={(event) => setJobId(event.target.value)}
        >
          <option value=''>Tüm ilanlar</option>
          {jobs.map((job) => (
            <option key={job.id || ''} value={job.id || ''}>
              {job.title}
            </option>
          ))}
        </select>
      )}

      {processes && (
        <select
          className='form-select form-select-solid w-auto'
          style={{minWidth: 200}}
          value={processId}
          onChange={(event) => setProcessId(event.target.value)}
        >
          <option value=''>Tüm süreçler</option>
          {processes.map((process) => (
            <option key={process.id || ''} value={process.id || ''}>
              {process.name}
            </option>
          ))}
        </select>
      )}

      {statusOptions && (
        <select
          className='form-select form-select-solid w-auto'
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value=''>Tüm durumlar</option>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {tags && (
        <select
          className='form-select form-select-solid w-auto'
          style={{minWidth: 170}}
          value={tagId}
          onChange={(event) => setTagId(event.target.value)}
        >
          <option value=''>Tüm etiketler</option>
          {tags.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name}
            </option>
          ))}
        </select>
      )}

      {hasDropdowns && (
        <>
          <button
            type='button'
            className='btn btn-primary'
            onClick={() => onFilter({jobId, processId, status, tagId})}
          >
            <i className='bi bi-funnel'></i>
            Filtrele
          </button>
          {hasSelection && (
            <button type='button' className='btn btn-light' onClick={handleClear}>
              Temizle
            </button>
          )}
        </>
      )}
    </div>
  )
}
